import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_API_URL || '';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Set base URL and credentials for all axios requests
    axios.defaults.baseURL = API_URL;
    axios.defaults.withCredentials = true;
    
    // Check for stored token on mount
    const token = localStorage.getItem('access_token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`);
      const data = response.data;
      const status = data?.approval_status || 'approved';
      if (status === 'pending' || status === 'rejected') {
        localStorage.removeItem('access_token');
        delete axios.defaults.headers.common['Authorization'];
        setUser(null);
        return;
      }
      setUser(data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    // Ensure baseURL is set before login request
    axios.defaults.baseURL = API_URL;

    const response = await axios.post(`${API_URL}/api/auth/login`, {
      username,
      password,
    });

    const { access_token } = response.data;
    localStorage.setItem('access_token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

    await fetchUser();
    return response.data;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API_URL}/api/auth/register`, userData);
    return response.data;
  };

  const logout = async () => {
    try {
        await axios.post(`${API_URL}/api/auth/logout`);
    } catch(e) {}
    localStorage.removeItem('access_token');
    localStorage.removeItem('selectedCollection');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  const isApproved = () => {
    const status = user?.approval_status || 'approved';
    return status === 'approved';
  };

  const isAdmin = () => {
    return user?.role === 'admin' || user?.is_superuser;
  };

  const canUpload = () => {
    // Admin and Editor can upload
    return ['admin', 'editor'].includes(user?.role) || user?.is_superuser;
  };

  const canProcess = () => {
    // Admin and Editor can process articles
    return ['admin', 'editor'].includes(user?.role) || user?.is_superuser;
  };

  const canEdit = () => {
    // Viewer can only view, Editor and Admin can edit
    return ['admin', 'editor'].includes(user?.role) || user?.is_superuser;
  };

  const value = {
    user,
    loading,
    login,
    logout,
    register,
    // Re-fetch the current user from /api/auth/me. Used by Profile after
    // PUT /profile or POST /avatar so we replace the synchronous-feeling
    // `window.location.reload()` pattern (UX audit 2026-05-26 §2.13).
    refreshUser: fetchUser,
    isAdmin,
    canUpload,
    canProcess,
    canEdit,
    isApproved,
    isAuthenticated: !!user && (user?.approval_status || 'approved') === 'approved',
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
